<script>
	import { afterUpdate,onMount } from 'svelte';
	import { interventions, dataset, capacity, regionLabel, positionTooltip } from '../stores.js';

	export let active;
	import Template from '../components/Template.svelte';
	import DonutChart from '../components/DonutChart.svelte';
	import CapacityCell from '../components/CapacityCell.svelte';
	import InterventionHeroGraphic from '../components/InterventionHeroGraphic.svelte';
  	import CapacityLegend from '../components/CapacityLegend.svelte';
	
	$: globe = $dataset ? $dataset.globe : false;
	$: regions = $dataset ? $dataset.regions : false;
	let tooltip;

	onMount(async () => {
		tooltip = document.getElementById("scoreapp") ? document.getElementById("scoreapp").querySelector(".tooltip") : null;

		if (!tooltip && document.getElementById("scoreapp")) {
			tooltip = document.createElement("div");
			tooltip.classList.add('tooltip');
			document.getElementById("scoreapp").appendChild(tooltip);
		}

		let interventionsSlider = new window.Glide('.interventions-slider', {
			type: 'slider',
			bound: 1,
			perView: 5,
			rewind: false,
			gap: 32,
			breakpoints: {
				768: {
					perView: 2
				},
				540: {
					perView: 1,
					gap: 16
				}
			}
		});
		interventionsSlider.mount();

		let interventionsGlobalSlider = new window.Glide('.interventions-global-slider', {
			type: 'slider',
			bound: 1,
			perView: 5,
			rewind: false,
			gap: 32,
			breakpoints: {
				768: {
					perView: 2
				},
				540: {
					perView: 1,
					gap: 16
				}
			}
		});
		interventionsGlobalSlider.mount();

		let interventionsCompareSlider = new window.Glide('.interventions-compare-slider', {
			type: 'slider',
			bound: 1,
			perView: 5,
			rewind: false,
			gap: 32,
			breakpoints: {
				768: {
					perView: 2
				},
				540: {
					perView: 1,
					gap: 16
				}
			}
		});
		interventionsCompareSlider.mount();

		interventionsSlider.on('run', (e) => {
			interventionsGlobalSlider.go(e.direction);
			interventionsCompareSlider.go(e.direction);
		});
		interventionsGlobalSlider.on('run', (e) => {
			interventionsSlider.go(e.direction);
			interventionsCompareSlider.go(e.direction);
		});
		interventionsCompareSlider.on('run', (e) => {
			interventionsSlider.go(e.direction);
			interventionsGlobalSlider.go(e.direction);
		});
	});
	afterUpdate(()=>{
		document.querySelectorAll('[data-capacity]').forEach((a)=>{
			a.onmouseenter = ()=>{
				hover = a.dataset.capacity;
			};
			a.onmouseleave = ()=>{
				hover = 0;
			};
		});
	});

	$: compare = 'afr';
	$: comparevar = regions ? regions[compare] : false;
	$: hover = 0;

	const trendIndicatorHTML = (trend = 'up', size = 'cell') => {
		const allowedTrends = ['up', 'down', 'flat'];
		const allowedSizes = ['sm', 'cell', 'base', 'lg', '2xl'];
		const resolvedTrend = allowedTrends.includes(trend) ? trend : 'up';
		const resolvedSize = allowedSizes.includes(size) ? size : 'cell';

		return `<div class="trend-indicator" data-trend="${resolvedTrend}" data-size="${resolvedSize}" aria-hidden="true"></div>`;
	};

	function changeRegion(){
		document.getElementById('regions-to-compare').checked = false;
	}

	function capacityDistributionTooltip(source, int, cap, label) {
		const values = source ? source['r_' + int.code] : null;
		const count = source ? source.count : 0;
		const quantity = values && values[cap] ? values[cap] : 0;
		const percent = count > 0 ? Math.round((quantity / count) * 100) : 0;

		let oldvalues= source ? source['r_'+int.code+'_round1'] : null;
		const oldquantity = oldvalues && oldvalues[cap] ? oldvalues[cap] : 0;
		const oldpercent = source.compared_count > 0 ? Math.round((oldquantity / source.compared_count) * 100) : 0;
		let trend = oldpercent==percent?'flat':oldpercent>percent?'up':'down';

		return `<header>
					<h4 class="tooltip-subtitle">${label}</h4>
					<h3 class="tooltip-title dynamic-color">${capacity(cap)} capacity</h3>
				</header>
				<section>
					<div class="flex middle-xs">
			    		<p class="tooltip-highlight-title">${percent}%</p>
						<span class="mr05">&nbsp;</span>
						${trendIndicatorHTML(trend, 'cell')}
					</div>
			    	<p class="tooltip-subtitle">${quantity} of ${count}</p>
				</section>`;
	}

	function showCapacityTooltip(event, source, int, label) {
		const sourceEvent = event.detail.sourceEvent;
		const capacityValue = event.detail.capacity;

		hover = capacityValue;

		if (!tooltip || !sourceEvent || !capacityValue) return;

		tooltip.classList.add('active');
		tooltip.removeAttribute('data-size');
		tooltip.dataset.capacity = capacityValue;
		tooltip.innerHTML = capacityDistributionTooltip(source, int, capacityValue, label);
		positionTooltip(tooltip, sourceEvent);
	}

	function moveCapacityTooltip(event) {
		if (!tooltip || !event.detail.sourceEvent) return;
		positionTooltip(tooltip, event.detail.sourceEvent);
	}

	function hideCapacityTooltip() {
		hover = 0;
		if (!tooltip) return;
		tooltip.classList.remove('active');
	}

</script>
<Template active={active}>
	<header class="interventions-hero">
		<div class="container-fluid wrap">
			<div class="row">
				<div class="col-xs-12">
					<nav aria-label="Breadcrumb">
						<ol class="breadcrumb">
							<li><a href="#/">SCORE Home</a></li>
							<li><span aria-current="page">Interventions</span></li>
						</ol>
					</nav>
				</div>
			</div>
			<div class="row">
				<div class="col-xs-12 col-md-6">
					<h2>SCORE Essential Interventions</h2>
					<p>Track worldwide progress in health data systems capacity to monitor and achieve the Sustainable Development Goals (SDGs).</p>
				</div>
				<div class="col-xs-12 col-md-6">
					<div class="interventions-hero__graphic">
						<InterventionHeroGraphic />
					</div>
				</div>
			</div>
		</div>
	</header>
	<article class="interventions-page-content data-capacity-{hover}">
	    <div class="container-fluid wrap big-card">

	        <input type="checkbox" id="interventions-compare-region" class="hide-input">

			<div class="row">
				<div class="col-xs-12">
					<h3 class="big-card-title mb4"><strong>Global results</strong> ({globe.count} countries)</h3>
				</div>
			</div>

	        <div class="row">
	            <div class="col-xs-12">
	                <div class="interventions-slider">
	                    <div class="slider-items" data-glide-el="track">
	                        <ul class="glide__slides">
								{#each interventions as int}
	                            <li class="glide__slide">
	                                <a href="#/intervention/{int.code}" class="letter-module" data-intervention="{int.id}">
	                                    <div class="letter">{@html int.svg}</div>
	                                    <p>{@html int.title}</p>
	                                </a>
	                            </li>
	                            {/each}
	                        </ul>
	                    </div>
	                </div>
	            </div>
	        </div>

	        <div class="row">
	            <div class="col-xs-12">
	                
	                <div class="interventions-global-slider">
	                    <div class="slider-items" data-glide-el="track">
	                        <ul class="glide__slides">
    							{#each interventions as int}
                                <li class="glide__slide">
                                    <figure class="donut-graph">
										<DonutChart
											data={globe ? globe['r_' + int.code] : []}
											value={globe ? globe[int.code] : '--'}
											capacityLevel={globe ? globe[int.code] : 0}
											previousCapacityLevel={globe ? globe[int.code+'_round1'] : null}
											showTrend={true}
											size={190}
											on:capacityhover={(event) => showCapacityTooltip(event, globe, int, 'Global results')}
											on:capacitymove={moveCapacityTooltip}
											on:capacityleave={hideCapacityTooltip}
										/>
                                    </figure>
                                    <div class="capacity-cells">
    									{#each [1,2,3,4,5] as i}
										<CapacityCell
											capacity={i}
											previousCapacity={globe ? (globe[int.code+'_round1']) : 0}
											value={globe ? `${Math.round(globe['r_' + int.code][i] / globe.count * 100)}%` : '0%'}
											showLabel={false}
											showTrend={false}
										/>
    			                    	{/each}
                                    </div>
									<a  href="#/intervention/{int.code}" class="btn btn-tertiary">view details</a>
                                </li>
                                {/each}
	                        </ul>
	                    </div>
	                    <div class="glide__bullets" data-glide-el="controls[nav]">
	                        <button class="glide__bullet" data-glide-dir="=0"></button>
	                        <button class="glide__bullet" data-glide-dir="=1"></button>
	                        <button class="glide__bullet" data-glide-dir="=2"></button>
	                        <button class="glide__bullet" data-glide-dir="=3"></button>
	                        <button class="glide__bullet" data-glide-dir="=4"></button>
	                      </div>
	                </div>
	            </div>
	        </div>
			
	        <div class="row interventions-compare-menu">
	            <div class="col-xs-12 flex flex-wrap center-xs middle-xs mt2 mb2 gap-md">

	                <!-- Selector -->
	                <fieldset class="drop-selector">
	                    <input type="checkbox" id="regions-to-compare">
	                    <label for="regions-to-compare">
	                        <legend data-value="{compare}">
	                            <span>{regionLabel(compare)}</span>
	                        </legend>
	                    </label>
	                    <ul class="drop-list drop-list-default">
	                    	{#each Object.keys(regions).sort() as r}
	                        <li>
	                            <input type="radio" name="regions-to-compare" value="{r}" data-label="{regionLabel(r)}" id="r_{r}" bind:group={compare} on:change={changeRegion}>
	                            <label for="r_{r}">
	                                <span>{regionLabel(r)}</span>
	                            </label>
	                        </li>
	                        {/each}
	                    </ul>
	                </fieldset>
	                <label for="interventions-compare-region" class="btn btn-secondary">Close Comparison</label>
	            </div>
	        </div>

	        <div class="row">
	            <div class="col-xs-12">
	                
	                <div class="interventions-compare-slider">
	                    <div class="slider-items" data-glide-el="track">
	                        <ul class="glide__slides">
								{#each interventions as int}
	                            <li class="glide__slide">
	                                <figure class="donut-graph">
										<DonutChart
											data={comparevar ? comparevar['r_' + int.code] : []}
											value={comparevar ? comparevar[int.code] : '--'}
											capacityLevel={comparevar ? comparevar[int.code] : 0}
											previousCapacityLevel={comparevar ? comparevar[int.code+'_round1'] : null}
											showTrend={true}
											size={190}
											on:capacityhover={(event) => showCapacityTooltip(event, comparevar, int, regionLabel(compare))}
											on:capacitymove={moveCapacityTooltip}
											on:capacityleave={hideCapacityTooltip}
										/>
                                    </figure>
	                                <div class="capacity-cells">
										{#each [1,2,3,4,5] as i}
										<CapacityCell
											capacity={i}
											value={comparevar ? `${Math.round(comparevar['r_' + int.code][i] / comparevar.count * 100)}%` : '0%'}
											showLabel={false}
											showTrend={false}
										/>
				                    	{/each}
	                                </div>
	                            </li>
	                            {/each}
	                        </ul>
	                    </div>
	                </div>

	            </div>
	        </div>

	        <div class="row center-xs">
	            <div class="col-xs-12 center-xs mt2 mb1">
					<CapacityLegend />
	            </div>
	            <div class="col-xs-12 mt1 mb2">
	                <label for="interventions-compare-region" class="btn btn-primary compare-btn">Compare with a region</label>
	            </div>
	        </div>
	    </div>
	</article>
</Template>
